import { useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { UserPlus } from 'lucide-react';
import AuthShell from '../auth/AuthShell';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Spinner from '../../components/ui/Spinner';
import { getErrorMessage } from '../../lib/apiClient';
import { useAuthStore } from '../../store/authStore';
import {
  acceptInvitation,
  acceptInvitationAsNewUser,
  previewInvitation,
  tenantKeys,
} from './tenancyApi';
import { roleLabel } from './permissions';

const PASSWORD_RULE = /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s])/;

/**
 * Landing page for emailed invitations (/join/:token). Public:
 * - Already signed in → auto-accept (the token + email match authorize it).
 * - New email → set a password to create the account and join.
 * - Existing account, signed out → prompt to log in, then it auto-accepts.
 */
export default function JoinInvitePage() {
  const { token } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((s) => Boolean(s.accessToken));
  const setSession = useAuthStore((s) => s.setSession);
  const startedRef = useRef(false);

  const onJoined = () => {
    queryClient.invalidateQueries({ queryKey: tenantKeys.organizations() });
    queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    toast.success('Invitation accepted — welcome aboard!');
    navigate('/files', { replace: true });
  };

  // Anonymous flow needs the preview to decide new-user vs. existing-user.
  const previewQuery = useQuery({
    queryKey: ['invitation-preview', token],
    queryFn: () => previewInvitation(token),
    enabled: Boolean(token) && !isAuthenticated,
    retry: false,
  });

  const acceptMutation = useMutation({
    mutationFn: () => acceptInvitation(token),
    onSuccess: onJoined,
    onError: (err) => {
      // 409 = already a member (e.g. auto-joined at signup) — that IS success.
      if (err?.response?.status === 409) onJoined();
    },
  });

  const acceptNewMutation = useMutation({
    mutationFn: (values) =>
      acceptInvitationAsNewUser({ token, displayName: values.displayName, password: values.password }),
    onSuccess: (data) => {
      setSession(data);
      onJoined();
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Could not accept the invitation')),
  });

  // Signed in → accept straight away (once).
  const { mutate: acceptExisting } = acceptMutation;
  useEffect(() => {
    if (isAuthenticated && token && !startedRef.current) {
      startedRef.current = true;
      acceptExisting();
    }
  }, [isAuthenticated, token, acceptExisting]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({ defaultValues: { displayName: '', password: '', confirmPassword: '' } });
  const passwordValue = watch('password');

  // ---- Signed-in: accepting / result ----
  if (isAuthenticated) {
    const failed = acceptMutation.isError && acceptMutation.error?.response?.status !== 409;
    return (
      <AuthShell
        icon={<UserPlus className="h-6 w-6 text-brand-600" />}
        title="Joining organization"
        subtitle="Hold on while we accept your invitation."
      >
        {failed ? (
          <div className="space-y-4 text-center">
            <p className="text-sm text-red-600">
              {getErrorMessage(acceptMutation.error, 'This invitation could not be accepted')}
            </p>
            <p className="text-sm text-slate-500">
              Invitations are tied to the email address they were sent to — if you signed in with a
              different account, log out and sign in with the invited address.
            </p>
            <div className="flex justify-center gap-4 text-sm font-medium">
              <Link to="/login" className="text-brand-600 hover:underline">
                Switch account
              </Link>
              <Link to="/files" className="text-brand-600 hover:underline">
                Go to my files
              </Link>
            </div>
          </div>
        ) : (
          <div className="flex justify-center py-4">
            <Spinner />
          </div>
        )}
      </AuthShell>
    );
  }

  // ---- Anonymous: load the preview ----
  if (previewQuery.isLoading) {
    return (
      <AuthShell icon={<UserPlus className="h-6 w-6 text-brand-600" />} title="Invitation">
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      </AuthShell>
    );
  }

  if (previewQuery.isError || !previewQuery.data) {
    return (
      <AuthShell
        icon={<UserPlus className="h-6 w-6 text-brand-600" />}
        title="Invitation unavailable"
        subtitle="This invitation link is invalid or has expired."
      >
        <div className="text-center text-sm">
          <Link to="/login" className="font-medium text-brand-600 hover:underline">
            Go to sign in
          </Link>
        </div>
      </AuthShell>
    );
  }

  const preview = previewQuery.data;
  const workspaceSummary = preview.workspaces
    .map((w) => `${w.name}${w.role === 'admin' ? ' (Admin)' : ''}`)
    .join(', ');

  // Existing account, signed out → send them to log in; auto-accept on return.
  if (preview.userExists) {
    return (
      <AuthShell
        icon={<UserPlus className="h-6 w-6 text-brand-600" />}
        title={`Join ${preview.orgName}`}
        subtitle={`You've been invited to ${workspaceSummary || 'this organization'}.`}
      >
        <div className="space-y-4 text-center">
          <p className="text-sm text-slate-500">
            An account already exists for <span className="font-medium">{preview.email}</span>. Sign
            in to accept this invitation.
          </p>
          <Link to="/login" state={{ from: location }} className="inline-block">
            <Button>Sign in to accept</Button>
          </Link>
        </div>
      </AuthShell>
    );
  }

  // New user → set a password to create the account and join.
  return (
    <AuthShell
      icon={<UserPlus className="h-6 w-6 text-brand-600" />}
      title={`Join ${preview.orgName}`}
      subtitle={
        preview.inviterName
          ? `${preview.inviterName} invited you to ${workspaceSummary || 'the organization'}.`
          : `You've been invited to ${workspaceSummary || 'the organization'}.`
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit((v) => acceptNewMutation.mutate(v))} noValidate>
        <Input label="Email" value={preview.email} disabled readOnly />
        <Input
          label="Full name"
          autoComplete="name"
          error={errors.displayName?.message}
          {...register('displayName', {
            required: 'Name is required',
            maxLength: { value: 120, message: 'Name is too long' },
          })}
        />
        <Input
          label="Password"
          type="password"
          autoComplete="new-password"
          hint="8+ chars with upper, lower, number, and symbol."
          error={errors.password?.message}
          {...register('password', {
            required: 'Password is required',
            minLength: { value: 8, message: 'At least 8 characters' },
            validate: (v) => PASSWORD_RULE.test(v) || 'Include upper, lower, number, and a symbol',
          })}
        />
        <Input
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          error={errors.confirmPassword?.message}
          {...register('confirmPassword', {
            required: 'Please confirm your password',
            validate: (v) => v === passwordValue || 'Passwords do not match',
          })}
        />
        {preview.workspaces.length > 0 && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500">
            You&apos;ll get access to:{' '}
            {preview.workspaces.map((w, i) => (
              <span key={w.workspaceId}>
                {i > 0 && ', '}
                <span className="font-medium text-slate-700">{w.name}</span> ({roleLabel(w.role)})
              </span>
            ))}
          </div>
        )}
        <Button type="submit" className="w-full" loading={acceptNewMutation.isPending}>
          Set password &amp; join
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-slate-500">
        Already have an account?{' '}
        <Link to="/login" state={{ from: location }} className="font-medium text-brand-600 hover:text-brand-700">
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
}
